import axios from '@/axiosInterceptor';

const state = () => ({
  teams: [],
  selectedTeam: {},
  teamMembers: [],
  hasTeamLoaded: false,
  pagination: {
    currentPage: 1,
    totalPages: 1,
    totalRecords: 0,
  },
  is_team_action_loading: false,
  team_user_update_loading: false,
  team_company_id: '',
  existingTeams: [],
  team_users_loading: false,
  team_refetch_loading: false,
  initially_loading_team: false,
});

// actions
const actions = {
  async getTeams(
    { commit, state },
    { page = 1, id, query = '', refetch = false }
  ) {
    if (refetch) {
      commit('setTeamsRefetchLoading', true);
    }
    try {
      let res = await axios.get(
        process.env.API_MONITOR +
          '/api/v1/teams?q[company_id_eq]=' +
          id +
          `&q[roots]=1&page=${page}&per_page=50&q[s]=name%20asc${query}`
      );

      if (refetch) {
        const combinedTeams = [...state.teams, ...res.data];

        commit('setTeams', combinedTeams);
      } else {
        commit('setTeams', res.data);
      }
      commit('setPagination', res?.headers);

      return res;
    } catch (e) {
      let status = e.response?.status;
      if (
        axios.isAxiosError(e) &&
        status &&
        [401, 403, 404, 422].includes(status)
      ) {
        return e.response;
      } else {
        throw e;
      }
    } finally {
      commit('setTeamsRefetchLoading', false);
    }
  },
  async getTeamById({ commit }, { id, initial = false }) {
    commit('setInitiallyLoadingTeam', initial);

    try {
      let res = await axios.get(
        process.env.API_MONITOR + '/api/v1/teams/' + id
      );
      commit('setSelectedTeam', res.data);
      commit('setHasTeamLoaded', true);
      return res;
    } catch (e) {
      let status = e.response?.status;
      if (
        axios.isAxiosError(e) &&
        status &&
        [401, 403, 404, 422].includes(status)
      ) {
        return e.response;
      } else {
        throw e;
      }
    } finally {
      commit('setInitiallyLoadingTeam', false);
    }
  },
  async createTeam({ commit, dispatch, state }, { body }) {
    commit('setTeamActionLoading', true);

    const params = {
      team: body,
    };
    try {
      const res = await axios.post(
        `${process.env.API_MONITOR}/api/v1/teams`,
        params
      );
      if ([200, 201, 204].includes(res?.status) && res.data) {
        dispatch('getTeams', { id: state.team_company_id });
        commit('setAddToExistingTeams', res.data);
      }
      return res;
    } catch (e) {
      let status = e.response?.status;
      if (
        axios.isAxiosError(e) &&
        status &&
        [401, 403, 404, 422].includes(status)
      ) {
        return e.response;
      } else {
        throw e;
      }
    } finally {
      commit('setTeamActionLoading', false);
    }
  },
  async editTeam({ commit, dispatch, state }, { id, body }) {
    commit('setTeamActionLoading', true);

    const params = {
      team: body,
    };
    try {
      const res = await axios.patch(
        `${process.env.API_MONITOR}/api/v1/teams/${id}`,
        params
      );
      if ([200, 201, 204].includes(res?.status)) {
        dispatch('getTeams', { id: state.team_company_id });
        commit('setUpdateExistingTeams', res.data);
      }
      return res;
    } catch (e) {
      let status = e.response?.status;
      if (
        axios.isAxiosError(e) &&
        status &&
        [401, 403, 404, 422].includes(status)
      ) {
        return e.response;
      } else {
        throw e;
      }
    } finally {
      commit('setTeamActionLoading', false);
    }
  },
  async addUsersToTeam({ commit, dispatch, state }, { id, body }) {
    commit('setTeamActionLoading', true);

    const params = {
      team: {
        team_users_attributes: body,
      },
    };
    try {
      const res = await axios.patch(
        `${process.env.API_MONITOR}/api/v1/teams/${id}`,
        params
      );

      if ([200, 201, 204].includes(res?.status) && res.data) {
        dispatch('getTeams', { id: state.team_company_id });
        commit('setUpdateExistingTeams', res.data);
      }
      return res;
    } catch (e) {
      let status = e.response?.status;
      if (
        axios.isAxiosError(e) &&
        status &&
        [401, 403, 404, 422].includes(status)
      ) {
        return e.response;
      } else {
        throw e;
      }
    } finally {
      commit('setTeamActionLoading', false);
    }
  },
  async bulkMoveUsers(
    { commit, dispatch, state },
    { id, user_ids, destination_team_id }
  ) {
    commit('setTeamActionLoading', true);

    const params = {
      user_ids,
      destination_team_id,
    };
    try {
      const res = await axios.post(
        `${process.env.API_MONITOR}/api/v1/teams/${id}/bulk_move_users`,
        params
      );

      if ([200, 201, 204].includes(res?.status)) {
        dispatch('getTeams', { id: state.team_company_id });
        await commit('setRemoveFromExistingTeams', [id, destination_team_id]);

        // refetch to get updated users
        const updatedTeamRes = await dispatch('getTeamById', id);
        await commit('setAddToExistingTeams', updatedTeamRes?.data);
      }
      return res;
    } catch (e) {
      let status = e.response?.status;
      if (
        axios.isAxiosError(e) &&
        status &&
        [401, 403, 404, 422].includes(status)
      ) {
        return e.response;
      } else {
        throw e;
      }
    } finally {
      commit('setTeamActionLoading', false);
    }
  },
  async removeTeam({ commit, dispatch, state }, { id }) {
    commit('setTeamActionLoading', true);

    try {
      let res = await axios.delete(
        process.env.API_MONITOR + '/api/v1/teams/' + id
      );

      if ([200, 201, 204].includes(res?.status)) {
        dispatch('getTeams', { id: state.team_company_id });
      }
      return res;
    } catch (e) {
      let status = e.response?.status;
      if (
        axios.isAxiosError(e) &&
        status &&
        [401, 403, 404, 422].includes(status)
      ) {
        return e.response;
      } else {
        throw e;
      }
    } finally {
      commit('setTeamActionLoading', false);
    }
  },
  async addTeamMember({ commit }, { id, team_users_attributes }) {
    try {
      let res = await axios.patch(
        process.env.API_MONITOR + '/api/v1/teams/' + id,
        {
          team: {
            team_users_attributes,
          },
        }
      );
      if ([200, 201, 204].includes(res?.status) && res.data) {
        // dispatch('getTeams', { id: state.team_company_id });
        commit('setTeamMembers', res.data?.team_users);
      }
      return res;
    } catch (e) {
      let status = e.response?.status;
      if (
        axios.isAxiosError(e) &&
        status &&
        [401, 403, 404, 422].includes(status)
      ) {
        return e.response;
      } else {
        throw e;
      }
    }
  },
  async removeTeamMember(
    { commit, dispatch, state },
    { id, team_users_attributes }
  ) {
    commit('setTeamActionLoading', true);

    try {
      let res = await axios.patch(
        process.env.API_MONITOR + '/api/v1/teams/' + id,
        {
          team: {
            team_users_attributes: team_users_attributes,
          },
        }
      );

      if ([200, 201, 204].includes(res?.status)) {
        dispatch('getTeams', { id: state.team_company_id });
        commit('setUpdateExistingTeams', res.data);
      }
      return res;
    } catch (e) {
      let status = e.response?.status;
      if (
        axios.isAxiosError(e) &&
        status &&
        [401, 403, 404, 422].includes(status)
      ) {
        return e.response;
      } else {
        throw e;
      }
    } finally {
      commit('setTeamActionLoading', false);
    }
  },
  async getTeamMembers({ commit, state }, { id, searchTerm, combine = false }) {
    commit('setTeamMembersLoading', true);

    let query = '';
    if (searchTerm) {
      query = `?q[user_email_or_user_first_name_or_user_last_name_cont]=${searchTerm}`;
    }

    try {
      let res = await axios.get(
        process.env.API_MONITOR + `/api/v1/teams/${id}/team_users${query}`
      );

      if (combine) {
        commit('setTeamMembers', [...state.teamMembers, ...res.data]);
      } else {
        commit('setTeamMembers', res.data);
      }
      return res;
    } catch (e) {
      let status = e.response?.status;
      if (
        axios.isAxiosError(e) &&
        status &&
        [401, 403, 404, 422].includes(status)
      ) {
        return e.response;
      } else {
        throw e;
      }
    } finally {
      commit('setTeamMembersLoading', false);
    }
  },
  async updateTeamUser({ commit, dispatch }, { id, body }) {
    commit('setTeamUserUpdateLoading', true);

    const params = {
      team_user: body,
    };

    try {
      const res = await axios.patch(
        `${process.env.API_MONITOR}/api/v1/team_users/${id}`,
        params
      );

      if ([200, 201, 204].includes(res?.status) && res.data) {
        // refetch to get updated users
        const updatedTeamRes = await dispatch(
          'getTeamById',
          res?.data?.team_id
        );
        await commit('setUpdateExistingTeams', updatedTeamRes?.data);
      }

      return res;
    } catch (e) {
      let status = e.response?.status;
      if (
        axios.isAxiosError(e) &&
        status &&
        [401, 403, 404, 422].includes(status)
      ) {
        return e.response;
      } else {
        throw e;
      }
    } finally {
      commit('setTeamUserUpdateLoading', false);
    }
  },
};

// mutations
const mutations = {
  setTeams(state, data) {
    state.teams = data;
  },
  setSelectedTeam(state, data) {
    state.selectedTeam = data;
  },
  setTeamActionLoading(state, bool) {
    state.is_team_action_loading = bool;
  },
  setTeamMembers(state, data) {
    state.teamMembers = data;
  },
  setHasTeamLoaded(state, data) {
    state.hasTeamLoaded = data;
  },
  setPagination(state, headers) {
    state.pagination = {
      currentPage: headers.currentpage,
      totalPages: headers.totalpages,
      totalRecords: headers.totalitems,
    };
  },
  setTeamUserUpdateLoading(state, bool) {
    state.team_user_update_loading = bool;
  },
  setTeamCompanyId(state, data) {
    state.team_company_id = data;
  },
  setAddToExistingTeams(state, data) {
    state.existingTeams = [...state.existingTeams, data];
  },
  setUpdateExistingTeams(state, data) {
    const filteredTeams = state.existingTeams?.filter(
      (item) => item?.id !== data?.id
    );
    state.existingTeams = [...filteredTeams, data];
  },
  setRemoveFromExistingTeams(state, data) {
    const filteredTeams = state.existingTeams?.filter(
      (item) => !data.includes(item?.id)
    );
    state.existingTeams = filteredTeams;
  },
  setTeamMembersLoading(state, bool) {
    state.team_users_loading = bool;
  },
  setTeamsRefetchLoading(state, bool) {
    state.team_refetch_loading = bool;
  },
  setInitiallyLoadingTeam(state, bool) {
    state.initially_loading_team = bool;
  },
};

export default {
  namespaced: true,
  state,
  actions,
  mutations,
};
